import { Injectable } from '@angular/core';

/**
 * Shared service for unit conversion logic
 * Used by both review-changes and approval-flow components
 */
@Injectable({
    providedIn: 'root'
})
export class UnitConversionService {
    // Track selected unit per field per operation
    // Key format: "operationId:fieldName" -> unit (e.g., "operation-0:component_length" -> "ft")
    private selectedUnits: { [key: string]: string } = {};

    // Default units for common field types
    private defaultUnits: { [key: string]: string } = {
        'length': 'ft',
        'depth': 'ft',
        'diameter': 'inch',
        'weight': 'lb_per_ft'
    };

    /**
     * Detect unit variants from converted_values object
     * Returns a map of base field names to their available units
     * Example: { "component_length": ["ft", "m"], "component_od": ["inch", "m"] }
     */
    public detectUnitVariants(convertedValues: any): { [baseField: string]: string[] } {
        const variants: { [baseField: string]: string[] } = {};
        
        if (!convertedValues || typeof convertedValues !== 'object') {
            return variants;
        }

        // Pattern: "field_name_in_unit" or "field name in unit"
        // Examples: component_length_in_ft, component_od_in_inch, weight_per_length_in_lb_per_ft, Hole Size in Inches
        for (const key in convertedValues) {
            // Skip if value is null/undefined
            if (convertedValues[key] === null || convertedValues[key] === undefined) {
                continue;
            }

            // Match patterns like "_in_ft", "_in_m", "_in_inch", " in ft", " in m", " in Inches"
            const underscoreMatch = key.match(/^(.+?)_in_(.+)$/i);
            const spaceMatch = key.match(/^(.+?)\s+in\s+(.+)$/i);
            
            if (underscoreMatch) {
                const baseField = underscoreMatch[1]; // "component_length", "weight_per_length"
                const unit = this.normalizeUnit(underscoreMatch[2]); // "ft", "m", "lb_per_ft"
                
                if (!variants[baseField]) {
                    variants[baseField] = [];
                }
                if (!variants[baseField].includes(unit)) {
                    variants[baseField].push(unit);
                }
            } else if (spaceMatch) {
                const baseField = spaceMatch[1].replace(/\s+/g, '_').toLowerCase();
                const unit = this.normalizeUnit(spaceMatch[2]);
                
                if (!variants[baseField]) {
                    variants[baseField] = [];
                }
                if (!variants[baseField].includes(unit)) {
                    variants[baseField].push(unit);
                }
            }
        }

        return variants;
    }

    /**
     * Check if a field has unit variants
     */
    public hasUnitVariants(fieldName: string, record: any): boolean {
        if (!record || !fieldName) {
            return false;
        }

        const convertedValues = record.converted_values;
        if (!convertedValues) {
            return false;
        }

        const variants = this.detectUnitVariants(convertedValues);
        const normalizedFieldName = this.normalizeFieldNameForComparison(fieldName);
        
        // Check if this field has variants
        for (const baseField in variants) {
            if (this.normalizeFieldNameForComparison(baseField) === normalizedFieldName) {
                return variants[baseField].length > 1;
            }
        }

        return false;
    }

    /**
     * Get available units for a field
     */
    public getAvailableUnits(fieldName: string, record: any): string[] {
        if (!record || !fieldName) {
            return [];
        }

        const convertedValues = record.converted_values;
        if (!convertedValues) {
            return [];
        }

        const variants = this.detectUnitVariants(convertedValues);
        const normalizedFieldName = this.normalizeFieldNameForComparison(fieldName);
        
        for (const baseField in variants) {
            if (this.normalizeFieldNameForComparison(baseField) === normalizedFieldName) {
                return variants[baseField];
            }
        }

        return [];
    }

    /**
     * Get selected unit for a field in a specific operation
     */
    public getSelectedUnit(operationId: string, fieldName: string, record: any): string {
        const key = `${operationId}:${this.normalizeFieldName(fieldName)}`;
        
        // Return stored selection if exists
        if (this.selectedUnits[key]) {
            return this.selectedUnits[key];
        }

        // Otherwise return first available unit
        const availableUnits = this.getAvailableUnits(fieldName, record);
        if (availableUnits.length > 0) {
            return availableUnits[0];
        }

        // Fallback to default based on field type
        return this.getDefaultUnitForField(fieldName);
    }

    /**
     * Set selected unit for a field in a specific operation
     */
    public setSelectedUnit(operationId: string, fieldName: string, unit: string): void {
        const key = `${operationId}:${this.normalizeFieldName(fieldName)}`;
        this.selectedUnits[key] = unit;
    }

    /**
     * Get value for selected unit
     */
    public getValueForUnit(operationId: string, fieldName: string, record: any): any {
        if (!record) {
            return null;
        }

        const convertedValues = record.converted_values;
        if (!convertedValues) {
            // Fallback to direct field value
            return record[fieldName];
        }

        const selectedUnit = this.getSelectedUnit(operationId, fieldName, record);
        const normalizedFieldName = this.normalizeFieldName(fieldName);

        // Build the key to look up in converted_values
        // Try both underscore and space patterns
        const underscoreKey = `${normalizedFieldName}_in_${selectedUnit}`;
        const spaceKey = normalizedFieldName.replace(/_/g, ' ') + ` in ${selectedUnit}`;
        
        // Try exact match first (underscore format)
        if (convertedValues.hasOwnProperty(underscoreKey)) {
            return convertedValues[underscoreKey];
        }
        
        // Try space format with proper casing
        for (const key in convertedValues) {
            if (key.toLowerCase() === spaceKey.toLowerCase()) {
                return convertedValues[key];
            }
        }

        // Try with case-insensitive underscore format
        for (const key in convertedValues) {
            if (key.toLowerCase() === underscoreKey.toLowerCase()) {
                return convertedValues[key];
            }
        }
        
        // Try normalizing both keys to same format (remove spaces and underscores)
        const normalizedExpectedKey = underscoreKey.toLowerCase().replace(/[\s_]+/g, '');
        for (const key in convertedValues) {
            const normalizedKey = key.toLowerCase().replace(/[\s_]+/g, '');
            if (normalizedKey === normalizedExpectedKey) {
                return convertedValues[key];
            }
        }

        // Try with unit variations (e.g., "inch" vs "inches")
        const unitVariations: { [key: string]: string[] } = {
            'inch': ['inch', 'inches', 'in'],
            'ft': ['ft', 'feet', 'foot'],
            'm': ['m', 'meters', 'meter'],
            'kg': ['kg', 'kilograms', 'kilogram'],
            'lb': ['lb', 'pounds', 'pound']
        };
        
        if (unitVariations[selectedUnit]) {
            for (const altUnit of unitVariations[selectedUnit]) {
                const altUnderscoreKey = `${normalizedFieldName}_in_${altUnit}`;
                const altSpaceKey = normalizedFieldName.replace(/_/g, ' ') + ` in ${altUnit}`;
                
                // Case-insensitive search
                for (const key in convertedValues) {
                    if (key.toLowerCase() === altUnderscoreKey.toLowerCase() || 
                        key.toLowerCase() === altSpaceKey.toLowerCase()) {
                        return convertedValues[key];
                    }
                }
            }
        }

        // Final fallback: Search by pattern matching field name + selected unit
        // This handles cases like "Hole Size in Inches" where we're looking for "hole size in inch"
        const fieldNameNormalized = normalizedFieldName.toLowerCase().replace(/_/g, '');
        const selectedUnitNormalized = selectedUnit.toLowerCase();
        
        for (const key in convertedValues) {
            const keyNormalized = key.toLowerCase();
            // Check if key contains the field name and unit pattern
            if (keyNormalized.includes('in') && fieldNameNormalized) {
                const parts = keyNormalized.split(/\s+in\s+/);
                if (parts.length === 2) {
                    const keyField = parts[0].replace(/[\s_]/g, '');
                    const keyUnit = parts[1].replace(/[\s_]/g, '');
                    
                    // Check if both field and unit match
                    if (keyField === fieldNameNormalized) {
                        // Check if the unit matches (considering variations)
                        const unitNormalized = selectedUnitNormalized.replace(/[\s_]/g, '');
                        const keyUnitVariations = this.getUnitVariations(keyUnit);
                        
                        if (keyUnitVariations.includes(unitNormalized) || keyUnit === unitNormalized) {
                            return convertedValues[key];
                        }
                    }
                }
            }
        }

        // Fallback to base field
        return record[fieldName] || record[normalizedFieldName];
    }

    /**
     * Get all variations of a unit (normalized)
     */
    private getUnitVariations(unitStr: string): string[] {
        const normalized = unitStr.toLowerCase().replace(/[\s_]/g, '');
        const variations = [normalized];
        
        // Add reverse mappings
        if (normalized === 'inches' || normalized === 'inch' || normalized === 'in') {
            variations.push('inch', 'inches', 'in');
        } else if (normalized === 'feet' || normalized === 'foot' || normalized === 'ft') {
            variations.push('ft', 'feet', 'foot');
        } else if (normalized === 'meters' || normalized === 'meter' || normalized === 'm') {
            variations.push('m', 'meters', 'meter');
        }
        
        return variations;
    }

    /**
     * Get display label for unit
     */
    public getUnitDisplayLabel(unit: string): string {
        const displayMap: { [key: string]: string } = {
            'ft': 'ft',
            'm': 'm',
            'inch': 'in',
            'inches': 'in',
            'lb_per_ft': 'lb/ft',
            'kg_per_m': 'kg/m',
            'lb': 'lb',
            'kg': 'kg'
        };
        
        return displayMap[unit] || unit;
    }

    /**
     * Normalize field name (remove spaces, underscores, lowercase)
     */
    private normalizeFieldName(name: string): string {
        return name.toLowerCase().replace(/[\s_]+/g, '_');
    }

    /**
     * Normalize field name for cross-source matching
     * Example: "Holesize", "Hole Size", "hole_size" -> "holesize"
     */
    private normalizeFieldNameForComparison(name: string): string {
        return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    /**
     * Normalize unit name
     */
    private normalizeUnit(unit: string): string {
        if (!unit) return '';
        
        const normalized = unit.toLowerCase().trim();
        
        // Don't normalize compound units like lb_per_ft
        if (normalized.includes('_per_') || normalized.includes('per')) {
            return normalized.replace(/\s+/g, '_');
        }
        
        // Normalize simple units - handle both singular and plural forms
        if (normalized === 'inches' || normalized === 'in') return 'inch';
        if (normalized === 'feet' || normalized === 'foot') return 'ft';
        if (normalized === 'meters' || normalized === 'meter') return 'm';
        if (normalized === 'kilograms' || normalized === 'kilogram') return 'kg';
        if (normalized === 'pounds' || normalized === 'pound') return 'lb';
        
        return normalized;
    }

    /**
     * Get default unit based on field name/type
     */
    private getDefaultUnitForField(fieldName: string): string {
        const normalized = fieldName.toLowerCase();
        
        if (normalized.includes('length') || normalized.includes('depth')) {
            return 'ft';
        }
        if (normalized.includes('diameter') || normalized.includes('_od') || normalized.includes('_id')) {
            return 'inch';
        }
        if (normalized.includes('weight')) {
            return 'lb_per_ft';
        }
        
        return 'ft'; // Default fallback
    }

    /**
     * Check if a key represents a unit variant field that should be hidden
     */
    public isUnitVariantKey(key: string): boolean {
        const lowerKey = key.toLowerCase();
        
        // Hide converted_values object itself
        if (lowerKey === 'converted_values') {
            return true;
        }
        
        // Check for unit patterns (both underscore and space-separated)
        return lowerKey.includes('_in_ft') || 
               lowerKey.includes('_in_m') || 
               lowerKey.includes('_in_inch') || 
               lowerKey.includes('_in_inches') || 
               lowerKey.includes('_in_lb_') ||
               lowerKey.includes('_in_kg_') ||
               lowerKey.includes('_in_ft') ||
               lowerKey.includes(' in ft') || 
               lowerKey.includes(' in m') || 
               lowerKey.includes(' in inch') ||
               lowerKey.includes(' in inches') ||
               lowerKey.includes(' in feet') ||
               lowerKey.includes(' in meters') ||
               lowerKey.includes(' in lb') ||
               lowerKey.includes(' in kg') ||
               /\s+in\s+(ft|m|inch|inches|feet|meters|lb|kg|lb_per_ft|kg_per_m)/i.test(key);
    }

    /**
     * Clear all stored unit selections
     */
    public clearSelections(): void {
        this.selectedUnits = {};
    }
}
